import { useEffect, useState } from "react";
import { CircleDashed, Send } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { joinWaitlist } from "../lib/trace";

const ENDPOINTS = [
  ["GET", "/v1/tokens/{mint}", "Token overview: supply, program, authorities, metadata."],
  ["GET", "/v1/tokens/{mint}/holders", "Top holders with labels and concentration metrics."],
  ["GET", "/v1/tokens/{mint}/risk", "Risk indicators with evidence, basis and source."],
  ["GET", "/v1/wallets/{address}/activity", "Recent transactions and confirmed interactions."],
  ["GET", "/v1/wallets/{address}/links", "Inferred relationships, labelled with confidence."],
];

const SAMPLES = {
  curl: `curl https://api.trace.example/v1/tokens/<MINT>/risk \\
  -H "Authorization: Bearer <API_KEY>"`,
  javascript: `const res = await fetch(
  "https://api.trace.example/v1/tokens/<MINT>/risk",
  { headers: { Authorization: "Bearer <API_KEY>" } }
);
const report = await res.json();`,
  python: `import requests

r = requests.get(
    "https://api.trace.example/v1/tokens/<MINT>/risk",
    headers={"Authorization": "Bearer <API_KEY>"},
)
report = r.json()`,
};

const RESPONSE = `{
  "mint": "<MINT>",
  "fetched_at": "2026-01-01T00:00:00Z",
  "signals": [
    {
      "id": "freeze_authority",
      "level": "high",
      "basis": "confirmed",
      "source": "solana_rpc",
      "evidence": ["Freeze authority: <ADDRESS>"]
    }
  ]
}`;

const Waitlist = () => {
  const [email, setEmail] = useState("");
  const [useCase, setUseCase] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await joinWaitlist({ email, use_case: useCase });
      toast.success("You're on the early-access list.");
      setEmail("");
      setUseCase("");
    } catch (err) {
      toast.error(err.response?.status === 422 ? "Please enter a valid email address." : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-xl border border-trace-line bg-trace-card p-6" data-testid="api-waitlist-form">
      <h2 className="text-lg font-medium text-white">Request early access</h2>
      <p className="text-sm text-trace-muted">We'll email you when the API opens. No spam, no wallet details.</p>
      <label className="sr-only" htmlFor="wl-email">Email</label>
      <input id="wl-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="waitlist-email-input"
        className="w-full rounded-lg border border-trace-line bg-trace-panel px-4 py-2.5 text-sm text-white placeholder:text-trace-dim focus:border-trace-green/50 focus:outline-none" />
      <label className="sr-only" htmlFor="wl-use">Use case</label>
      <textarea id="wl-use" rows={3} value={useCase} onChange={(e) => setUseCase(e.target.value)} placeholder="What would you build? (optional)" data-testid="waitlist-usecase-input"
        className="w-full resize-none rounded-lg border border-trace-line bg-trace-panel px-4 py-2.5 text-sm text-white placeholder:text-trace-dim focus:border-trace-green/50 focus:outline-none" />
      <button type="submit" disabled={busy} data-testid="waitlist-submit-button"
        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-trace-green py-2.5 text-sm font-semibold text-trace-bg transition-opacity disabled:opacity-60">
        <Send size={14} /> {busy ? "Submitting…" : "Join the list"}
      </button>
    </form>
  );
};

export default function Developers() {
  useEffect(() => { document.title = "Developer API (Planned) — TRACE"; }, []);
  return (
    <main className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" data-testid="developers-page">
      <span data-testid="api-coming-soon-badge" className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-amber-300">
        <CircleDashed size={12} /> Coming soon · planned
      </span>
      <h1 className="mt-5 text-4xl font-semibold tracking-[-0.03em] text-white sm:text-5xl">TRACE Developer API</h1>
      <p className="mt-4 max-w-2xl text-base text-trace-muted md:text-lg">
        A proposed API for developers and trading tools to access supported TRACE analytics. It is not available yet. Endpoints, schema and the base URL below are illustrative and subject to change.
      </p>
      <div className="mt-14 grid gap-8 lg:grid-cols-[1.5fr_1fr]">
        <div className="min-w-0 space-y-8">
          <section className="rounded-xl border border-trace-line bg-trace-card/60" data-testid="api-endpoints-list">
            <h2 className="border-b border-trace-line px-6 py-4 text-base font-medium text-white">Proposed endpoints</h2>
            {ENDPOINTS.map(([m, p, d]) => (
              <div key={p} className="flex flex-col gap-1 border-b border-trace-line/60 px-6 py-4 last:border-0 sm:flex-row sm:items-center sm:gap-4">
                <span className="w-10 font-mono text-xs text-trace-green">{m}</span>
                <code className="break-all font-mono text-sm text-white">{p}</code>
                <span className="text-sm text-trace-muted sm:ml-auto sm:text-right">{d}</span>
              </div>
            ))}
          </section>
          <Tabs defaultValue="curl" data-testid="api-docs-preview-tab">
            <TabsList className="bg-trace-panel">
              {Object.keys(SAMPLES).map((k) => <TabsTrigger key={k} value={k} data-testid={`api-tab-${k}`} className="font-mono text-xs">{k}</TabsTrigger>)}
            </TabsList>
            {Object.entries(SAMPLES).map(([k, v]) => (
              <TabsContent key={k} value={k}>
                <pre className="overflow-x-auto rounded-xl border border-trace-line bg-trace-panel p-5 font-mono text-xs leading-relaxed text-slate-200">{v}</pre>
              </TabsContent>
            ))}
          </Tabs>
          <div>
            <p className="eyebrow mb-2">Example response (illustrative)</p>
            <pre className="overflow-x-auto rounded-xl border border-trace-line bg-trace-panel p-5 font-mono text-xs leading-relaxed text-trace-muted">{RESPONSE}</pre>
          </div>
        </div>
        <aside className="lg:sticky lg:top-24 lg:self-start"><Waitlist /></aside>
      </div>
    </main>
  );
}
